import React, { useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { ChevronDown } from "lucide-react";
import { cn } from "@/lib/utils";
import { Button } from "@/components/ui/button";
import { ProfileAvatar } from "@/components/common/ProfileAvatar";
import { MessageActionsPopover } from "@/components/common/MessageActionsPopover";
import { EmailContentFrame } from "@/components/common/EmailContentFrame";
import {
	formatFullDateTime,
	getParticipantName,
	isHTMLContent,
	splitQuotedHistory,
	toSnippet,
} from "./utils";
import type { InboxConversation, InboxMessage, InboxParticipant, MatchedCandidate, SaveEntity } from "@/types/inbox";

interface EmailMessageCardProps {
	message: InboxMessage;
	conversation: InboxConversation;
	participantsById: Record<string, InboxParticipant>;
	isDarkMode: boolean;
	defaultExpanded: boolean;
	matchedCandidate?: MatchedCandidate | null;
	/** Host entity to pre-link saves to; falls back to the matched candidate. */
	currentEntity?: SaveEntity;
}

/**
 * One email in a thread, rendered as a proper mail card: sender header with
 * full timestamp, sanitized HTML body via EmailContentFrame, quoted reply
 * history collapsed behind a toggle. Older messages start collapsed to a
 * single scannable line.
 */
export const EmailMessageCard: React.FC<EmailMessageCardProps> = ({
	message,
	conversation,
	participantsById,
	isDarkMode,
	defaultExpanded,
	matchedCandidate,
	currentEntity,
}) => {
	const { t, i18n } = useTranslation();
	const [expanded, setExpanded] = useState(defaultExpanded);
	const [showQuoted, setShowQuoted] = useState(false);

	const outgoing = message.is_sender === 1;
	const participant = message.sender_attendee_id
		? participantsById[message.sender_attendee_id]
		: undefined;
	const senderName = outgoing
		? t("inbox.you", "You")
		: getParticipantName(participant) ||
			message.sender?.name ||
			message.sender?.email ||
			t("inbox.unknownSender", "Unknown");
	const senderEmail = participant?.email || message.sender?.email || "";

	const recipients = useMemo(
		() =>
			(conversation.participants || [])
				.filter((p) => p.role === "to")
				.map((p) => getParticipantName(p))
				.filter(Boolean)
				.join(", "),
		[conversation.participants]
	);

	const content = message.text || "";
	const html = isHTMLContent(content);
	const { main, hasQuotedHistory } = useMemo(
		() => (html ? splitQuotedHistory(content) : { main: content, hasQuotedHistory: false }),
		[content, html]
	);

	return (
		<article
			className={cn(
				"group rounded-sm border border-border bg-card text-card-foreground shadow-card transition-shadow animate-fade-in",
				expanded && "shadow-card-hover",
				message.optimistic && "opacity-80"
			)}
		>
			<button
				type="button"
				onClick={() => setExpanded((value) => !value)}
				className="flex w-full items-start gap-3 p-4 text-left focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring"
				aria-expanded={expanded}
			>
				<ProfileAvatar
					src={participant?.picture_url}
					alt={senderName}
					className="h-9 w-9 shrink-0"
					participantId={participant?.id}
					conversationId={conversation.id}
				/>
				<div className="min-w-0 flex-1">
					<div className="flex items-baseline justify-between gap-3">
						<span className="truncate text-sm font-semibold">{senderName}</span>
						<span className="shrink-0 text-xs tabular-nums text-muted-foreground">
							{message.optimistic
								? t("inbox.sending", "Sending…")
								: formatFullDateTime(message.timestamp, i18n.language)}
						</span>
					</div>
					{expanded ? (
						<p className="mt-0.5 truncate text-xs text-muted-foreground">
							{senderEmail && <span>{senderEmail}</span>}
							{recipients && (
								<span>
									{senderEmail && " · "}
									{t("inbox.to", "to")} {recipients}
								</span>
							)}
						</p>
					) : (
						<p className="mt-0.5 truncate text-xs text-muted-foreground">{toSnippet(content, 120)}</p>
					)}
				</div>
				<ChevronDown
					className={cn(
						"mt-1 h-4 w-4 shrink-0 text-muted-foreground transition-transform",
						expanded && "rotate-180"
					)}
				/>
			</button>

			{expanded && (
				<div className="border-t border-border px-4 pb-4 pt-3">
					{html ? (
						<>
							<EmailContentFrame content={showQuoted ? content : main} isDarkMode={isDarkMode} />
							{hasQuotedHistory && (
								<Button
									variant="ghost"
									size="sm"
									className="mt-2 h-7 gap-1 px-2 text-xs text-muted-foreground"
									onClick={() => setShowQuoted((value) => !value)}
								>
									<ChevronDown className={cn("h-3 w-3 transition-transform", showQuoted && "rotate-180")} />
									{showQuoted
										? t("inbox.hideQuoted", "Hide quoted history")
										: t("inbox.showQuoted", "Show quoted history")}
								</Button>
							)}
						</>
					) : (
						<div className="whitespace-pre-wrap break-words text-sm leading-relaxed">{content}</div>
					)}
					{!message.optimistic && (
						<div className="mt-2 flex justify-end opacity-0 transition-opacity group-hover:opacity-100">
							<MessageActionsPopover
								messageData={{
									id: message.id,
									text: content,
									subject: conversation.subject,
									messageType: conversation.conversation_type,
									participants: conversation.participants,
								}}
								currentEntity={
									currentEntity ??
									(matchedCandidate
										? { type: "candidates", id: matchedCandidate.id, name: matchedCandidate.name }
										: undefined)
								}
							/>
						</div>
					)}
				</div>
			)}
		</article>
	);
};
